import TotalCMS from '../totalcms';
import TotalField from './totalfield';
import TotalDispatcher from './dispatcher';
import FieldVisibility from './field-visibility';
import Identifier from './identifier';
import Checkbox from './checkbox';
import RadioField from './radio';
import Textarea from './textarea';
import NumberField from './number';
import PriceField from './price';
import ColorField from './color';
import DateField from './date';
import DeckField from './deck';
import PasswordField from './password';
import SelectField from './select';
import MultiSelectField from './multiselect';
import MultiCheckboxField from './multicheckbox';
import ListField from './list';
import RangeSlider from './range';
import StyledTextField from './styledtext';
import SVGField from './svg';
import ImageField from './image';
import GalleryField from './gallery';
import PropertiesField from './properties';
import CustomPropertiesField from './customProperties';
import SchemaPropertiesField from './schemaProperties';
import JSONField from './json';
import FileField from './file';
import DepotField from './depot';
import CodeField from './code';

// import Deck from './deck';
// import MarkdownField from './markdown';

//-----------------------------------------------
// Total CMS Form constructor
//-----------------------------------------------
export default class TotalForm {

    // Constructors
    constructor(formRef, options = {}) {
        this.form = this.setForm(formRef);
		formRef.totalform = this;
		this.fields = []; // Initialize early to prevent undefined errors on disabled forms

		if (formRef.dataset.disabled !== undefined) {
			return;
		}

		if (!formRef || !this.form) {
			console.error("form not found");
			return;
		}

		// Define option defaults
		const defaults = {
			actions : {
				new    : [],
				edit   : [],
				delete : [],
			}
		};
		this.options = Object.assign({}, defaults, options);

		if (this.form.dataset.newActions) {
			this.options.actions.new = JSON.parse(this.form.dataset.newActions);
		}
		if (this.form.dataset.editActions) {
			this.options.actions.edit = JSON.parse(this.form.dataset.editActions);
		}
		if (this.form.dataset.deleteActions) {
			this.options.actions.delete = JSON.parse(this.form.dataset.deleteActions);
		}
		this.delayActions = 2500;

		this.api = new TotalCMS({
			url: this.form.dataset.api,
		});
		this.route      = this.form.dataset.route;
		this.method     = this.form.dataset.method || "POST";
		this.id         = this.form.dataset.id||"";
		this.collection = this.form.dataset.collection;
		this.schema     = this.form.dataset.schema;
		this.states     = ["unsaved","success","error","processing"];
		this.state      = null;
		this.validated  = false;

		// Check if the form is a collection, schema or object
		this.type = this.form.dataset.form || "object";

		// Check if the form has an ID set
		// This is done before fields are created in case the ID is autogenerated
		const formIDSet = (typeof this.form.dataset.id !== "undefined" && this.form.dataset.id.length > 0);

		// If an ID is set, we are in edit mode
		if (formIDSet) this.setupEditMode();

		this.processFields();
		this.droplets = this.fields.filter(field => field.isDroplet());
		this.setupFieldsForEdit();

		this.dispatcher = new TotalDispatcher(this.form);
		this.visibility = new FieldVisibility(this.form, this.fields);

        this.eventListeners();
        this.registerButtons();
		this.visibility.initialize();

		if (this.form.classList.contains("autosave")) {
			this.autosave = true;
		}

		// Check if this form uses AJAX (default is true if not specified)
		const usesAjax = this.form.dataset.ajax !== "false";

		// Check if unsaved changes warning should be shown (default is true if not specified)
		const warnUnsaved = this.form.classList.contains("no-unsaved-warning") ? false : true;

		// Don't run the beforeunload event when inside iframes, for non-AJAX forms, or when explicitly disabled
		// Non-AJAX forms handle their own submission and don't need unsaved change warnings
		if (window === window.top && usesAjax && warnUnsaved) {
			window.onbeforeunload = e => {
				if (this.isUnsaved()) {
					e.preventDefault();
					const dialogText = "There are unsaved changes";
					e.returnValue = dialogText;
					return dialogText;
				}
			};
		}
    }

    //-------------------------
    // Utility Methods
    //-------------------------

	isDeckForm() {
		return this.type === "deck";
	}
	isObjectForm() {
		return this.type === "object";
	}
	isCollectionForm() {
		return this.type === "collection";
	}
	isSchemaForm() {
		return this.type === "schema";
	}
	isTemplateForm() {
		return this.type === "template";
	}
    // Check to see if the object is a HTML node.
    isDomNode(node){
        return node && typeof node === "object" && "nodeType" in node && node.nodeType === 1;
    }
    // Set the form via a DOM element or selector string
    setForm(formRef) {
        switch(typeof formRef) {
            case "string":
                return document.getElementById(formRef);
            case "object":
                if (this.isDomNode(formRef)){
                    return formRef;
                }
                break;
        }
        return null;
    }

	setId(id) {
		this.id = id;
		this.form.dataset.id = id;
	}

	getId() {
		if (this.isEditMode()) return this.id;

		// Filter for main form's ID field, excluding subfields (like deck item IDs)
		const idField = this.fields.filter(field => {
			return field.property === "id" && !field.isSubField();
		}).shift();
		if (idField) return idField.getValue();

		console.warn("ID field not found");
		return null;
	}

    //-------------------------
    // Init Form
    //-------------------------
    processFields() {
		const fields = Array.from(this.form.getElementsByClassName("form-field"));
		const fieldObjects = [];
        fields.forEach(field => {
			// Skip fields inside <template> elements (e.g., deck templates)
			if (field.closest('template')) {
				return;
			}

			// skip if field is already processed
			if (field.totalfield && !field.totalfield.isSubField()) {
				fieldObjects.push(field.totalfield);
				return;
			}

			const object = this.generateFieldObject(field);
            if (object === null || object.isSubField()) return; // if the object is not set, skip it
            fieldObjects.push(object);

			// Mark as dirty
        });
        this.fields = fieldObjects;
    }

    registerButtons() {
		// Save button action is handled by the TotalFormManager

		const deleteButtons = Array.from(this.form.querySelectorAll("button.cms-delete,a.cms-delete,.cms-delete a,.cms-delete button"));
        deleteButtons.forEach(button => {
            button.addEventListener("click", event => {
                event.preventDefault();
				this.delete();
            });
        });
    }

    generateFieldObject(field) {
        const options = JSON.parse(field.dataset.options||"{}");
        options.form = this;

        switch (field.dataset.type) {
			case "id":
			case "slug":
                return new Identifier(field, options);

			case "text":
			case "time":
            case "url":
			case "hidden":
			case "email":
			case "phone":
				return new TotalField(field, options);

			case "textarea":
				return new Textarea(field, options);

            case "checkbox":
            case "toggle":
                return new Checkbox(field, options);

			case "multicheckbox":
                return new MultiCheckboxField(field, options);

            case "radio":
                return new RadioField(field, options);

            case "number":
                return new NumberField(field, options);

            case "price":
                return new PriceField(field, options);

            case "color":
                return new ColorField(field, options);

            case "date":
			case "datetime":
                return new DateField(field, options);

            case "select":
                return new SelectField(field, options);

            case "multiselect":
                return new MultiSelectField(field, options);

            case "list":
                return new ListField(field, options);

			case "password":
				return new PasswordField(field, options);

            case "range":
                return new RangeSlider(field, options);

			case "styledtext":
                return new StyledTextField(field, options);

            case "svg":
                return new SVGField(field, options);

			case "image":
				return new ImageField(field,options);

			case "gallery":
				return new GalleryField(field,options);

			case "json":
				return new JSONField(field,options);

			case "file":
				return new FileField(field,options);

			case "depot":
				return new DepotField(field,options);

			case "code":
				return new CodeField(field,options);

			case "deck":
                return new DeckField(field, options);

			// case "markdown":
            //     return new MarkdownField(field, options);

			case "properties":
				return new PropertiesField(field,options);

			case "customProperties":
				return new CustomPropertiesField(field,options);

			case "schemaProperties":
				return new SchemaPropertiesField(field,options);

            default:
                console.warn("Unknown field",field);
				return new TotalField(field, options);
        }
    }

    //-------------------------
    // Submit functions
    //-------------------------
    eventListeners() {
		// Prevent the default form submission
		if (this.method.toUpperCase() !== "GET") {
			this.form.addEventListener("submit", event => event.preventDefault());
		}
		this.form.addEventListener("field-change", e => {
			// Ignore change events when form is processing
			// If a field is in focus on submit, a change event is triggered
			if (this.isProcessing()) return;
			this.unsaved();
			if (this.autosave) this.save();
		});
		this.form.addEventListener("field-error", e => this.error(e.detail.message, e.detail.field));
    }

	validate() {
		this.fields.forEach(field => field.validate());

		if (this.form.checkValidity()) {
			return true;
		}
		// If the form is invalid, display the custom validation error messages
		this.form.reportValidity();
		return false;
	}

	closeDialog() {
		const dialog = this.form.querySelector("dialog[open]");
		if (dialog) {
			dialog.dialog ? dialog.dialog.close() : dialog.close();
		}
	}

	save() {
		if (!this.validate()) {
			this.validated = false;
			this.error('Please fix validation errors before saving.');
			return;
		}
		if (this.isError()) {
			this.error('Error in form, cannot save.');
			return;
		}
		this.validated = true;
		this.closeDialog();
        this.processing();
        this.api.postAPI(this.route, this.generateData(), this.method)
            .then(response => this.afterSave(response))
            .catch(error => this.error(error));
    }

    delete() {
        // Only delete if editing object
        if (!this.isEditMode()) return;

        if (window.confirm("Are you sure that you want to delete this? This cannot be undone.")) {
            this.validated = true;
            this.processing();

			let deleteAPI = `/collections/${this.collection}/${this.id}`;

			// if (this.isDeckForm()) {
			// 	deleteAPI = `/{collection}/${this.id}/{property}/deck/{itemId}`;
			// }
			if (this.isSchemaForm()) {
				deleteAPI = `/schemas/${this.id}`;
			}
			if (this.isCollectionForm()) {
				deleteAPI = `/collections/${this.id}`;
			}
			if (this.isTemplateForm()) {
				deleteAPI = this.route;
			}

            this.api.postAPI(deleteAPI, {}, "DELETE")
                .then(response => this.runDeleteActions(response))
                .catch(error => this.error(error));
        }
    }

    afterSave(response) {
        if (!response) return;

		const unsavedDroplets = this.droplets.filter(field => field.isUnsaved());
        if (unsavedDroplets.length > 0) {
            return this.saveDroplets(() => this.afterSaveAction(response));
        }
		this.afterSaveAction(response);
    }

    afterSaveAction(response) {
		const runEditActions = this.isEditMode();
        this.success(response);
        const waitUntilSaved = () => {
            // wait until all saving states have completed
            if (this.isSuccess()) {
				// Mark all fields as saved
				this.fields.forEach(field => field.saved());
                // run actions
                return runEditActions ? this.runEditActions() : this.runNewActions();
            }
            // Check again
            window.setTimeout(waitUntilSaved,250);
        };
        waitUntilSaved();
    }

    async runAction(action) {
        switch (action.action) {
			case "back":
				const referrerUrl = new URL(document.referrer);
				if (window.history.length > 1 && document.referrer && referrerUrl.hostname === window.location.hostname) {
					document.location = document.referrer;
				}
				break;
			case "mailer":
				await this.api.postAPI('/action/mailer', {
					data     : this.generateData(),
					mailerId : action.mailerId,
				});
				break;
            case "redirect":
                document.location = action.link;
                break;
            case "redirect-object":
				const link = decodeURI(action.link);
				if (link.match("{id}"))  {
					document.location = link.replace("{id}", this.id);
				} else {
					document.location = link + this.id;
				}
                break;
            case "refresh":
                location.reload(true);
                break;
			case "ajax":
			case "webhook":
				const response = await fetch(action.link, {
					method : 'POST',
					mode   : 'cors',
					body   : JSON.stringify(this.generateData()),
				});
				if (!response.ok) {
					throw new Error(`Action ${action.action} failed: ${response.statusText}`);
				}
				break;
			default:
				console.warn(`Unknown action type: ${action.action}`);
        }
    }

	/**
	 * Run an array of actions sequentially.
	 * By default, if an action fails, subsequent actions won't execute.
	 * Set `continue: true` on an action to continue execution even if it fails.
	 *
	 * @param {Array} actions - Array of action objects to execute
	 */
	async runActions(actions) {
		if (!Array.isArray(actions) || actions.length === 0) {
			return;
		}

		for (const action of actions) {
			try {
				await this.runAction(action);
			} catch (error) {
				console.error(`Action execution failed:`, error);

				// If continue is true, log the error but keep going
				if (action.continue === true) {
					console.warn(`Action failed but continuing due to continue: true`, action);
					continue;
				}

				// Otherwise, stop execution and show error
				this.error(error.message || 'Action execution failed');
				throw error; // Re-throw to prevent further execution
			}
		}
	}

    runNewActions() {
		setTimeout(() => this.runActions(this.options.actions.new), this.delayActions);
    }

    runEditActions() {
		setTimeout(() => this.runActions(this.options.actions.edit), this.delayActions);
    }

	runDeleteActions() {
		setTimeout(() => this.runActions(this.options.actions.delete), 250);
    }

    //-------------------------
    // Form States
    //-------------------------
	isEditMode() {
		return (["PUT", "PATCH"].includes(this.method.toUpperCase()) && this.form.classList.contains("edit-mode"));
    }

    setupEditMode() {
		if (this.isEditMode()) return;

		// Only setup edit mode for forms that have an ID (object/collection/schema forms)
		// Simple forms without IDs should always use POST
		if (!this.id || this.id.length === 0) return;

		// Deck forms have their route fully set by PHP (includes parent object ID and optionally item ID)
		// Don't modify the route for deck forms
		if (this.type === "deck") return;

		// Set the method to PUT or PATCH for editing existing objects
		this.method = this.form.dataset.method||"PUT";
		if (this.method.toUpperCase() === "POST") this.method = "PUT";

		this.form.classList.add("edit-mode");

		// Update the API to the edit endpoint
		if (!this.route.endsWith(this.id)) {
			this.route = `${this.route}/${this.id}`;
			this.form.dataset.route = this.route;
		}
    }

	setupFieldsForEdit() {
		if (!this.isEditMode()) {
			return;
		}

		// The ID cannot be changed in edit form
		const idField = this.fields.filter(field => field.property === "id").shift();
		// if ID field is hidden, these may not exist
		if (idField.disable) {
			idField.disable();
			idField.lock();
		}

		// Update the droplets to autoupload
		this.droplets.forEach(field => field.droplet.autoProcessQueue());
    }

	uploadComplete() {
		if (this.isUnsaved()) return;
		this.success();
	}

	changeState(newState, details = {}) {
		this.state = newState;
		if (newState) {
			this.form.classList.add(newState);
			this.form.dispatchEvent(new CustomEvent(newState, {detail: details}));
		}
		// filer the newState and remove all others
		const remove = this.states.filter(e => e !== newState);
		this.form.classList.remove(...remove);
	}

	clear() {
		this.changeState(null);
	}

	unsaved() {
		this.changeState("unsaved");
	}

	isUnsaved() {
		let unsaved = false;
		this.fields.forEach(field => { if (field.isUnsaved()) unsaved = true; });
        return unsaved;
    }

	processing() {
		this.changeState("processing");
    }

	isProcessing() {
        return this.state === "processing";
    }

    error(error, field = null) {
		this.changeState("error", {error:error});
		this.validated = false;

		// Include field information in error message if available
		if (field) {
			const fieldLabel = field.label || field.property || 'unknown field';
			const fieldProperty = field.property || 'unknown';
			console.error(`Form Error [${fieldProperty}]:`, error);
			console.error(`  Field Label: "${fieldLabel}"`);
			console.error(`  Field Element:`, field.input);
		} else {
			console.error("Form Error:", error);
		}
    }

	isError() {
		return this.state === "error";
	}

    success(response = null) {
		// Extract ID from response for new objects
		if (response && response.id && (!this.id || this.id.length === 0)) {
			this.id = response.id;
			this.form.dataset.id = response.id;
		}
		this.setupEditMode();
		this.changeState("success", response);
		this.validated = false;
		this.fields.forEach(field => field.saved());
    }

	isSuccess() {
		return this.state === "success";
	}


    //-------------------------
    // Droplet Interactions
    //-------------------------

    // We only want to process the droplet queue after the inital
    // post request to create the object has been saved
    saveDroplets(callback) {

        let dropletCount = this.droplets.filter(field => field.isUnsaved()).length;

        // If no unsaved droplets, execute callback immediately
        if (dropletCount === 0) {
            if (typeof callback === "function") callback();
            return;
        }

        const dropletComplete = (callback) => {
            // When there are multiple droplets, we need to ensure that the callback
            // is only processed once for the entire form submission.
            // Example: if there are 3 droplets, run after the 3rd time this has been triggered
            dropletCount--;
            if (dropletCount === 0) {
                if (typeof callback === "function") callback();
            }
        };

		this.droplets.forEach(field => {
			if (!field.isUnsaved()) return; // skip if the droplet is not unsaved
			field.updateAPIUrl(); // update the droplet API URL
			const droplet = field.droplet;
            if (droplet.isComplete()) {
                dropletComplete(callback);
                return;
            }
            droplet.onQueueComplete(() => dropletComplete(callback));
            droplet.autoProcessQueue(); // Enable auto processing for all queued files
            droplet.processQueue();
		});
    }

    //-------------------------
    // Generating Form Data
    //-------------------------

    generateData() {
        const data = {};
		this.fields.forEach(field => {
			if (field.isSubField()) return; // skip subfields
			data[field.property] = field.getValue();
		});
        return data;
    }
}
